import { CheckOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Button, List, Typography } from "antd";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../app/store";
import SuccessModal from "../../../components/common/SuccessModal";
import CustomModal from "../../../components/ui/CustomModal";
import { useUpdateUserForBankIdMutation } from "../../../features/cashCollection/cashCollectionSetup/cashCollectionSetupApi";
import { useGetUserListQuery } from "../../../features/portal/Products/productsApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import ConfirmModalLayout from "../../../layouts/ConfirmModalLayout";
import { CustomModalProps } from "../../../types";

const ReconcileModal = ({
  open,
  data,
  handleClose,
}: CustomModalProps & { data: any }) => {
  const { user } = useAppSelector((state) => state.auth);
  const projectId = localStorage.getItem("product_id");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string | null>(null);

  const {
    open: openConfirmModal,
    handleOpen: handleConfirmOpen,
    handleClose: handleConfirmClose,
  } = useModal<any, "CONFIRM">();

  const {
    open: openSuccessModal,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal<undefined, "SUCCESS">();

  const [updateUser, { isLoading, isError, error, isSuccess }] =
    useUpdateUserForBankIdMutation();

  const { data: getAllUserData } = useGetUserListQuery(
    {
      id: projectId, // ✅ move id here
      page: 1,
      limit: 1000000000000,
      companyId: user?.data?.company_id, // ✅ match query definition
      roleId: undefined, // if you don’t have it yet
    },
    {
      skip: !user?.data?.company_id || !projectId, // ✅ skip if missing
    }
  );

  const employeeOptions = getAllUserData?.data?.users?.map((item) => ({
    id: item.user.id,
    name: item.user.username,
    avatar: null,
    email: item.user.email,
    role: item.role?.roleName,
    project: item.project?.name,
    company: item.user.company?.company_name,
    status: item.status,
  }));

  const handleSendToApprove = () => {
    updateUser({ id: data?.id, data: { userId: selectedUserId } });
  };

  const handleCloseFunction = () => {
    handleClose();
    setSelectedUserId(null);
  };

  const handleOkay = () => {
    handleClose();
    handleConfirmClose();
    handleSuccessClose();
    setSelectedUserId(null);
    setSelectedUserName(null);
  };

  useEffect(() => {
    if (isSuccess) {
      handleClose();
      handleSuccessOpen({ type: "SUCCESS" });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useError(isError, error, "Reconcile failed.");
  return (
    <>
      <CustomModal
        width={380}
        title="Reconcile"
        open={open}
        handleClose={handleCloseFunction}
        footer={null}
      >
        <div className="overflow-y-auto max-h-[300px] my-4">
          <List
            dataSource={employeeOptions}
            renderItem={(item) => (
              <List.Item
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "8px 0",
                }}
                onClick={() => {
                  setSelectedUserId(item.id);
                  setSelectedUserName(item.name);
                }}
              >
                <Avatar
                  src={item?.avatar}
                  icon={!item?.avatar && <UserOutlined />}
                />
                <div className="ml-3 flex flex-col flex-1">
                  <Typography.Text strong>{item.name}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {item.company}
                  </Typography.Text>
                </div>
                {selectedUserId === item.id && (
                  <CheckOutlined style={{ color: "#1677ff", fontSize: 16 }} />
                )}
              </List.Item>
            )}
          />
        </div>
        <div className="flex items-center justify-end gap-3">
          <Button
            onClick={() => {
              handleCloseFunction();
            }}
          >
            Cancel
          </Button>
          <Button
            loading={isLoading}
            onClick={() => {
              handleConfirmOpen({ type: "CONFIRM" });
            }}
            type="primary"
            disabled={!selectedUserId}
          >
            Continue
          </Button>
        </div>
      </CustomModal>

      {openConfirmModal?.type === "CONFIRM" && (
        <ConfirmModalLayout
          title={`Reconcile Confirmation`}
          subTitle={`Are you sure you want to transfer from ${data?.user?.username} to ${selectedUserName}?`}
          buttonName={`Confirm`}
          handleButton={handleSendToApprove}
          open={openConfirmModal?.open}
          handleClose={handleConfirmClose}
          action
          buttonBgDeep
          loading={isLoading}
        ></ConfirmModalLayout>
      )}

      {openSuccessModal.type === "SUCCESS" && (
        <SuccessModal
          open={openSuccessModal?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={openSuccessModal?.data}
          title={`Successfully Reconcile Transferred`}
        />
      )}
    </>
  );
};

export default ReconcileModal;
